
import React from 'react';
import { User } from '@supabase/supabase-js';
import { Profile } from '@/hooks/useProfile';
import { useProfileForm } from './profile/useProfileForm';
import ProfileFormHeader from './profile/ProfileFormHeader';
import BasicInfoForm from './profile/form/BasicInfoForm';
import SkillsForm from './profile/form/SkillsForm';
import LanguagesForm from './profile/form/LanguagesForm';
import PortfolioSection from './profile/PortfolioSection';
import ExperienceSection from './profile/ExperienceSection';
import PortfolioUrlSection from './profile/PortfolioUrlSection';
import SocialMediaSection from './profile/SocialMediaSection';
import ProfileFormFooter from './profile/ProfileFormFooter';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

interface ProfileFormProps {
  user: User;
  initialProfile: Profile;
  onProfileUpdate: (updatedProfile: Profile) => void;
  targetUserId?: string;
}

const ProfileForm = ({ user, initialProfile, onProfileUpdate, targetUserId }: ProfileFormProps) => {
  const {
    profile,
    setProfile,
    formError,
    isUpdating,
    isUploading,
    avatarPreview,
    newSkill,
    newLanguage,
    handleAvatarChange,
    addSkill,
    removeSkill,
    addLanguage,
    removeLanguage,
    handlePortfolioChange,
    handleExperienceChange,
    handleSubmit,
    setNewSkill,
    setNewLanguage
  } = useProfileForm(user, initialProfile, onProfileUpdate, targetUserId);

  // This adapter function converts the string URL to the format expected by handleAvatarChange
  const handleAvatarUrl = (url: string) => {
    // Call the original handleAvatarChange directly with the URL
    handleAvatarChange(url);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <ProfileFormHeader 
        profile={profile}
        user={user}
        avatarPreview={avatarPreview}
        onAvatarChange={handleAvatarUrl}
        isUploading={isUploading}
      />
      
      {formError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}
      
      <BasicInfoForm 
        profile={profile}
        setProfile={setProfile}
      />

      <SkillsForm
        skills={profile.skills || []}
        newSkill={newSkill}
        profession={profile.profession}
        onNewSkillChange={setNewSkill}
        onAddSkill={addSkill}
        onRemoveSkill={removeSkill}
      />

      <LanguagesForm
        languages={profile.languages || []}
        newLanguage={newLanguage}
        onNewLanguageChange={setNewLanguage}
        onAddLanguage={addLanguage}
        onRemoveLanguage={removeLanguage}
      />

      <PortfolioSection
        portfolioItems={profile.portfolio_items || []}
        onPortfolioChange={handlePortfolioChange}
      />

      <ExperienceSection
        experienceItems={profile.experience_items || []}
        onExperienceChange={handleExperienceChange}
        user={user}
        skills={profile.skills}
      />

      <PortfolioUrlSection 
        portfolioUrl={profile.portfolio_url || ''}
        onPortfolioUrlChange={(value) => setProfile('portfolio_url', value)}
      />

      <SocialMediaSection
        instagramUsername={profile.instagram_username || ''}
        linkedinUsername={profile.linkedin_username || ''}
        onInstagramChange={(value) => setProfile('instagram_username', value)}
        onLinkedinChange={(value) => setProfile('linkedin_username', value)}
      />
      
      <ProfileFormFooter isUpdating={isUpdating || isUploading} />
    </form>
  );
};

export default ProfileForm;
